import React, { useState, useEffect } from "react";
import { CancelIcon, ChevronRightIcon, ConfirmIcon, FilterIcon, HideIcon, ICON_SIZE, ViewIcon } from "./actionIcons";
import ReactDOM from 'react-dom';
import fields from "./fields";
import { Button } from "@radix-ui/themes";
import useColumnVisibilityTour from './useColumnVisibilityTour';
import CustomTooltip from './JoyrideCustomTooltip';
import { TOUR_FLOATER_PROPS } from './JoyridePortal';
//Lazy (plan 5.1): keeps react-joyride out of the entry chunk; the tour is
//desktop-only and its render site is already gated on !isMobile.
const Joyride = React.lazy(() => import('react-joyride'));
import { HelpCircle, ChevronUp, ChevronDown } from 'lucide-react';
import useColumnPrefs from './useColumnPrefs';
import { useIsMobile } from './hooks/useMediaQuery';
import { pageCheckState } from './hooks/useRowSelection';
import { isColumnVisible } from "./columnVisibility";

function CommonThead({
    objectsArray,
    sortObjectsArray,
    sortOrder,
    fieldOrder,
    setFieldOrder,
    parentSheetName,
    filterData,
    filterObject,
    sheetName=null,
    // When true, renders a narrow empty <th> at the end to match a rowActions column.
    hasRowActions=false,
    // Optional multi-select (useRowSelection) plus the primary keys on this page.
    selection=null,
    pageIds=[],
    }) {

    const [contextMenuPosition, setContextMenuPosition] = useState(null);
    const [activeColumn, setActiveColumn] = useState(null);
    const [showColumnVisibilityDialog, setShowColumnVisibilityDialog] = useState(false);
    const { persistFieldOrder, applyVisibilityUpdate } = useColumnPrefs(parentSheetName, setFieldOrder);

    const moveItem = (array, fromIndex, toIndex) => {
        if (fromIndex < 0 || fromIndex >= array.length || toIndex < 0 || toIndex > array.length) {
            throw new Error("Invalid index");
        }
        const copy = [...array];
        const [item] = copy.splice(fromIndex, 1);
        // After removing fromIndex, elements shift left — adjust toIndex accordingly
        const adjustedTo = fromIndex < toIndex ? toIndex - 1 : toIndex;
        copy.splice(adjustedTo, 0, item);
        return copy;
    }

    // Helper function to check if a column is filtered
    const isColumnFiltered = (fieldName) => {
        if (!filterObject || !filterObject[fieldName]) {
            return false;
        }
        
        const filterData = filterObject[fieldName];
        
        // Check if filterType exists and has actual filter values
        if (!filterData.filterType) {
            return false;
        }
        
        // Check if there are actual filter values (not empty/null)
        const hasLowerValue = filterData.lowerValue !== undefined && 
                             filterData.lowerValue !== null && 
                             filterData.lowerValue !== '';
                             
        const hasUpperValue = filterData.upperValue !== undefined && 
                             filterData.upperValue !== null && 
                             filterData.upperValue !== '';
        
        return hasLowerValue || hasUpperValue;
    };

    // Helper function to get sort indicator
    const getSortIndicator = (fieldName) => {
        const order = sortOrder[fieldName];
        if (!order || order === 'none') return null;
        
        return (
            <span className={`sort-indicator sort-${order}`}>
                {order === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    // Helper function to get filter indicator
    const getFilterIndicator = (fieldName) => {
        if (!isColumnFiltered(fieldName)) return null;
        
        return (
            <span className="filter-indicator" title="Column is filtered">
                🔍
            </span>
        );
    };

    const handleDragOverTd = (e) => {
        e.preventDefault();
        const clientX = e.clientX;
        const target = e.currentTarget; // Always the <th>
        const rect = target.getBoundingClientRect();
        const width = rect.width;
        const left = rect.left;

        // Determine the half being hovered
        const half = (clientX - left) <= width / 2 ? "First" : "Second";

        // Reset classes first
        target.classList.remove("drag-hover-left", "drag-hover-right");
        const elmts = document.getElementsByClassName(`col-${target.dataset.name}`);
        for (let elmt of elmts) {
            elmt.classList.remove("drag-hover-left", "drag-hover-right");
        }
        // Add appropriate class
        if (half === "First") {
            target.classList.add("drag-hover-left");
            for (let elmt of elmts) {
                elmt.classList.add("drag-hover-left");
            }
        } else {
            target.classList.add("drag-hover-right");
            for (let elmt of elmts) {
                elmt.classList.add("drag-hover-right");
            }
        }
    };

    const handleDragLeaveTd = (e) => {
        const target = e.currentTarget; // Always the <th>
        // Remove drag hover classes when the cursor leaves
        target.classList.remove("drag-hover-left", "drag-hover-right");
        const elmts = document.getElementsByClassName(`col-${target.dataset.name}`);
        for (let elmt of elmts) {
            elmt.classList.remove("drag-hover-left", "drag-hover-right");
        }
    };

    const handleDrop = (e) => {
        const target = e.currentTarget; // currentTarget is always the <th>, even when dropping on a child element
        const clientX = e.clientX;
        const rect = target.getBoundingClientRect();
        const width = rect.width;
        const left = rect.left;
        // Ensure classes are removed on drop
        target.classList.remove("drag-hover-left", "drag-hover-right");
        const elmts = document.getElementsByClassName(`col-${target.dataset.name}`);
        for (let elmt of elmts) {
            elmt.classList.remove("drag-hover-left", "drag-hover-right");
        }
        // Determine the half being hovered
        const half = (clientX - left) <= width / 2 ? "First" : "Second";

        //Work by field NAME, not by the rendered index: hidden cost columns are
        //filtered out of the rendered order, so rendered indices don't line up
        //with the full stored order. Resolve names to full-array positions
        //inside the setter so hidden columns keep their place.
        const fromName = e.dataTransfer.getData("text/plain");
        const toName = target.dataset.name;
        if (!fromName || !toName || fromName === toName) return;
        setFieldOrder(prev => {
            const fromIndex = prev.findIndex(f => f.name === fromName);
            let toIndex = prev.findIndex(f => f.name === toName);
            if (fromIndex < 0 || toIndex < 0) return prev;
            if (half === "Second") toIndex += 1; // drop after the target
            const newArr = moveItem(prev, fromIndex, toIndex);
            persistFieldOrder(newArr); // Persists to DB and mirrors into GlobalContext
            return newArr;
        });
    };

    const handleDragStart = (e) => {
        // Add a class to indicate dragging
        e.target.classList.add("dragging");
        //Carry the field NAME so the drop can resolve it against the full order.
        e.dataTransfer.setData("text/plain", e.target.dataset.name);
    };

    const handleDragEnd = (e) => {
        // Remove the dragging class
        e.target.classList.remove("dragging");
    };

    const handleContextMenu = (e, columnName) => {
        e.preventDefault();
        setContextMenuPosition({ x: e.pageX, y: e.pageY });
        //Track by name, not rendered index, so hidden cost columns (filtered
        //out of the rendered order) don't shift which column this refers to.
        setActiveColumn({
            columnName,
            columnDataType: fields[columnName].dataType
        });
    };

    const hideContextMenu = () => {
        setContextMenuPosition(null);
        setActiveColumn(null);
    };

    const handleToggleColumnVisibilityDialog = () => {
        setShowColumnVisibilityDialog(true);
        hideContextMenu();
    };

    const handleHideColumn = () => {
        setFieldOrder(prev => {
            const newArr = prev.map(field =>
                field.name === activeColumn.columnName ? { ...field, hidden: true } : field
            );
            persistFieldOrder(newArr);
            return newArr;
        });
        hideContextMenu();
    };

    const handleSort = () => {
        sortObjectsArray(activeColumn.columnName);
        hideContextMenu();
    };

    return (
        <>
            <thead className="thead-inventory" id="main-table-head">
                <tr>
                    {selection && (
                        <th className="sheet-check-cell">
                            {/* Ticks or clears THIS PAGE, which is all it can honestly
                                promise: the sheet is server-paginated and the rows
                                beyond this page are not loaded. Selecting everyone is
                                the audience picker's job (Messages → Compose), not a
                                checkbox that would have to guess at the search filter. */}
                            <input
                                type="checkbox"
                                id="select-page-checkbox"
                                aria-label="Select all rows on this page"
                                checked={pageCheckState(pageIds, selection.ids) === 'all'}
                                ref={(el) => {
                                    if (el) el.indeterminate = pageCheckState(pageIds, selection.ids) === 'some';
                                }}
                                onChange={() => selection.togglePage(pageIds)}
                            />
                        </th>
                    )}
                    {
                        //If the sheet is one of "Inventory", "Committed", "Cash", "Credit", "CreditCleared",
                        //the first field should be "PrimaryImage", an image element
                        ["Inventory", "Committed", "Cash", "Credit", "CreditCleared"].includes(sheetName) && (
                            <th>
                                <div className="column-header">
                                    <span className="column-title">Image</span>
                                </div>
                            </th>)
                        
                    }
                    {
                        fieldOrder.map((fieldObj) => {
                            //One shared rule with CommonTBody and CommonDataView —
                            //buildDefaultOrder appends any row key it doesn't
                            //recognise and the sheet endpoints are SELECT *, so an
                            //unknown column must skip rather than take the sheet
                            //down. It also force-shows a filtered column; see
                            //columnVisibility.js.
                            if (isColumnVisible(fieldObj, filterObject)) {
                                const fieldName = fieldObj.name;
                                const isFiltered = isColumnFiltered(fieldName);
                                const isSorted = sortOrder[fieldName] && sortOrder[fieldName] !== 'none';
                                
                                return (
                                    <th 
                                        draggable
                                        onDragStart={handleDragStart}
                                        onDragOver={handleDragOverTd}
                                        onDragEnd={handleDragEnd}
                                        onDragLeave={handleDragLeaveTd}
                                        onDrop={handleDrop}
                                        onContextMenu={(e) => handleContextMenu(e, fieldObj.name)}
                                        //Touch devices have no right-click and no HTML5 drag:
                                        //a plain tap opens the same sort/filter/columns menu.
                                        //Desktop left-click stays a no-op (drag = reorder).
                                        onClick={(e) => {
                                            if (window.matchMedia('(pointer: coarse)').matches) {
                                                handleContextMenu(e, fieldObj.name);
                                            }
                                        }}
                                        id={"thid-" + fieldName}
                                        key={fieldObj.name}
                                        data-name={fieldObj.name}
                                        title={`${fields[fieldName]?.tooltip || ""}
(Drag left or right to rearrange the column, right click here for more options)`}
                                        className={`
                                            ${isFiltered ? 'filtered' : ''}
                                            ${isSorted ? 'sorted' : ''}
                                        `.trim()}
                                    >
                                        <div className="column-header">
                                            <span className="column-title">
                                                {fields[fieldName].displayName}
                                            </span>
                                            <div className="column-indicators">
                                                {getSortIndicator(fieldName)}
                                                {getFilterIndicator(fieldName)}
                                            </div>
                                        </div>
                                    </th>
                                )
                            } else {
                                return null;
                            }
                        })
                    }
                    {hasRowActions && <th style={{ width: '2rem' }} />}
                    {
                        contextMenuPosition !== null && ReactDOM.createPortal(
                            <ContextMenu
                                fieldName={activeColumn?.columnName}
                                fieldType={activeColumn?.columnDataType}
                                position={contextMenuPosition}
                                onHide={hideContextMenu}
                                onFilter={filterData}
                                filterObject={filterObject}
                                onSort={handleSort}
                                sortOrder={sortOrder}
                                onHideColumn={handleHideColumn}
                                onToggleColumnVisibilityDialog={handleToggleColumnVisibilityDialog}
                            />, 
                            document.body
                        )
                    }
                    {
                        showColumnVisibilityDialog && ReactDOM.createPortal(
                            <ColumnVisibilityDialog
                                fieldOrder={fieldOrder}
                                fields={fields}
                                onClose={() => setShowColumnVisibilityDialog(false)}
                                onUpdateVisibility={applyVisibilityUpdate}
                                moveItem={moveItem}
                            />,
                            document.body
                        )
                    }
                </tr>
            </thead>
        </>
    );
}

import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import * as PopoverPrimitive from "@radix-ui/react-popover";


function ContextMenu({ 
    fieldName, 
    fieldType, 
    position, 
    onHide, 
    onSort, 
    sortOrder,
    filterObject, 
    onFilter, 
    onHideColumn, 
    onToggleColumnVisibilityDialog 
}) {
    // State to manage selected filter type and values
    const [filterType, setFilterType] = useState(null);
    const [filterValue1, setFilterValue1] = useState("");
    const [filterValue2, setFilterValue2] = useState("");
    
    if (!position) return null;

    const fieldDataType = (fieldType === "integer" || fieldType === "decimal" || fieldType === "date") ? "numerical" : fieldType;

    // Determine current sort label
    const currentSort = sortOrder?.[fieldName];
    const sortLabel = currentSort === 'asc' ? 'Sort Desc' : 'Sort Asc';

    // Handle filter submission
    const handleApplyFilter = () => {
        if (filterType) {
            onFilter(fieldName, filterType, filterValue1, filterValue2);
            onHide(); // Hide the menu after applying the filter
        }
    };

    const clearFilter = () => {
        onFilter(fieldName); // Only one argument to clear the filter
        onHide();
    };

    const clearAllFilters = () => {
        onFilter(""); // Only one argument to clear the filter. Empty string to clear all filters
        onHide();
    };

    // Helper function to apply selected styling
    const getFilterItemStyle = (itemType) => {
        if (filterType === itemType) {
        return {
            backgroundColor: 'var(--theme-accent-tint)',
            borderLeft: '3px solid var(--theme-accent)',
            fontWeight: '600',
            color: 'var(--theme-accent-ink)'
        };
        }
        return {};
    };    

    // This creates the fixed positioned context menu at the clicked coordinates
    return (
        <div 
            className="fixed z-50"
            style={{ top: position.y, left: position.x }}
        >
            <PopoverPrimitive.Root open={true} onOpenChange={(open) => !open && onHide()}>
                <PopoverPrimitive.Anchor className="w-0 h-0" />
                <PopoverPrimitive.Content 
                    className="context-menu-content"
                    sideOffset={5}
                    avoidCollisions={true}
                    style={{ maxHeight: "400px", overflowY: "auto" }}
                >
                    {/* Sort Option */}
                    <div 
                        className="context-menu-item" 
                        onClick={onSort}
                    >
                        {sortLabel}
                    </div>

                    <div className="context-menu-separator" />

                    {/* Filter Options */}
                    <div className="context-menu-group">
                    <div className="context-menu-label">Filter</div>

                    <div 
                        className="context-menu-item" 
                        style={getFilterItemStyle("=")}
                        onClick={() => setFilterType("=")}
                    >
                        {fieldType === "string" ? "Text contains" : "Equals"}
                    </div>

                    {fieldDataType === "numerical" && (
                        <>
                        {[
                            { op: ">", label: fieldType === "date" ? "Date is after" : "Greater than" },
                            { op: ">=", label: fieldType === "date" ? "Date is on or after" : "Greater than or equal to" },
                            { op: "<", label: fieldType === "date" ? "Date is before" : "Less than" },
                            { op: "<=", label: fieldType === "date" ? "Date is on or before" : "Less than or equal to" },
                            { op: "between", label: fieldType === "date" ? "Date is between" : "Between" },
                        ].map(({ op, label }) => (
                            <div 
                            key={op} 
                            className="context-menu-item" 
                            style={getFilterItemStyle(op)}
                            onClick={() => setFilterType(op)}
                            >
                            {label}
                            </div>
                        ))}
                        </>
                    )}
                    </div>

                    {/* Filter Input Fields */}
                    {filterType && (
                        <div className="p-2 space-y-2">
                            <input
                                className="input-t"
                                type={(() => {
                                    switch(fieldType) {
                                        case "string": return "text";
                                        case "integer": return "number";
                                        case "decimal": return "number";
                                        case "date": return "date";
                                        default: return "text";
                                    }
                                })()}
                                value={filterValue1}
                                onChange={(e) => setFilterValue1(e.target.value)}
                                placeholder={fieldType === "date" ? "Select date" : "Enter value"}
                            />
                            
                            {filterType === "between" && (
                                <input
                                    className="input-t"
                                    type={fieldType === "date" ? "date" : "number"}
                                    value={filterValue2}
                                    onChange={(e) => setFilterValue2(e.target.value)}
                                    placeholder={fieldType === "date" ? "Select end date" : "Enter second value"}
                                />
                            )}
                            
                            <Button 
                                className="mt-2 w-full rounded py-2 px-4"
                                variant="soft"
                                size="2"
                                radius="medium"
                                onClick={handleApplyFilter}
                                style={{ width: "100%" }}
                            >
                                <FilterIcon size={ICON_SIZE} />
                                Apply Filter
                            </Button>
                        </div>
                    )}

                    {/* Clear Filter Button */}
                    {Object.getOwnPropertyNames(filterObject).length !== 0 && (
                        <div 
                            className="context-menu-item text-red-600" 
                            onClick={clearFilter}
                        >
                            Clear Filter
                        </div>
                    )}


                    {/* Clear All Filters Button */}
                    {Object.getOwnPropertyNames(filterObject).length !== 0 && (
                        <div 
                            className="context-menu-item text-red-600" 
                            onClick={clearAllFilters}
                        >
                            Clear All Filters
                        </div>
                    )}

                    <div className="context-menu-separator" />

                    {/* Column Visibility Options */}
                    <div 
                        className="context-menu-item" 
                        onClick={onToggleColumnVisibilityDialog}
                    >
                        Show/Hide Columns
                    </div>
                    <div 
                        className="context-menu-item" 
                        onClick={onHideColumn}
                    >
                        Hide This Column
                    </div>
                </PopoverPrimitive.Content>
            </PopoverPrimitive.Root>
        </div>
    );
}


export { ContextMenu };

function ColumnVisibilityDialog({ fieldOrder, fields, onClose, onUpdateVisibility, moveItem }) {
    const [localFieldOrder, setLocalFieldOrder] = useState([...fieldOrder]);
    const [searchTerm, setSearchTerm] = useState("");
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // Tour hook
    const { runTour, stepIndex, steps, handleStartTutorial, handleJoyrideCallback } = useColumnVisibilityTour();
    //Tours are desktop-only (MOBILE_PLAN.md 1.4)
    const isMobile = useIsMobile();

    //Reorder without drag-and-drop — the only way to reorder on touch devices,
    //and a keyboard-friendly alternative on desktop.
    const moveFieldBy = (fromIndex, delta) => {
        setLocalFieldOrder(prev => {
            const to = fromIndex + delta;
            if (fromIndex < 0 || to < 0 || to >= prev.length) return prev;
            const copy = [...prev];
            const [item] = copy.splice(fromIndex, 1);
            copy.splice(to, 0, item);
            return copy;
        });
    };

    // Handle checkbox change
    const handleCheckboxChange = (index) => {
        setLocalFieldOrder(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                hidden: !updated[index].hidden
            };
            return updated;
        });
    };

    // Apply changes and close dialog
    const handleApply = () => {
        onUpdateVisibility(localFieldOrder);
        onClose();
    };

    // Reset to original state
    const handleCancel = () => {
        onClose();
    };

    // Toggle all checkboxes
    const handleToggleAll = (showAll) => {
        setLocalFieldOrder(prev => 
            prev.map(field => ({
                ...field,
                hidden: !showAll
            }))
        );
    };

    // Handle drag start
    const handleDragStart = (e, index) => {
        console.log("Drag start:", index);
        setDraggedItem(index);
        e.target.classList.add("dragging");
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData("text/plain", index.toString());
        
        // Create and append ghost element
        const dragImg = document.createElement('div');
        dragImg.textContent = fields[localFieldOrder[index].name]?.displayName || localFieldOrder[index].name;
        dragImg.className = 'drag-ghost';
        dragImg.style.position = 'absolute';
        dragImg.style.top = '-1000px';
        document.body.appendChild(dragImg);
        e.dataTransfer.setDragImage(dragImg, 0, 0);
        
        // Clean up ghost element
        requestAnimationFrame(() => {
            document.body.removeChild(dragImg);
        });
    };

    // Handle drag over
    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        if (draggedItem === null || draggedItem === index) return;
        
        setDragOverIndex(index);
        
        const element = e.currentTarget;
        // Clear previous hover classes
        element.classList.remove("drag-over-top", "drag-over-bottom");
        
        // Determine if hovering over top or bottom half
        const rect = element.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const halfHeight = rect.height / 2;
        
        if (y < halfHeight) {
            element.classList.add("drag-over-top");
        } else {
            element.classList.add("drag-over-bottom");
        }
    };

    // Handle drag leave
    const handleDragLeave = (e) => {
        e.currentTarget.classList.remove("drag-over-top", "drag-over-bottom");
        setDragOverIndex(null);
    };

    // Handle drop
    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        const element = e.currentTarget;
        element.classList.remove("drag-over-top", "drag-over-bottom");
        
        if (draggedItem === null || draggedItem === dropIndex) return;
        
        // Determine drop position (before or after the target element)
        const rect = element.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const halfHeight = rect.height / 2;
        
        // Simpler calculation for finalDropIndex
        const isBelowHalf = y >= halfHeight;
        const finalDropIndex = isBelowHalf ? dropIndex + 1 : dropIndex;
        
        console.log("Drop:", {
            from: draggedItem,
            to: finalDropIndex,
            position: isBelowHalf ? "after" : "before"
        });
        
        // Only perform the move if indices are valid
        if (draggedItem !== finalDropIndex) {
            setLocalFieldOrder(prev => {
                try {
                    // Create a fresh copy to avoid mutation issues
                    const newArray = [...prev];
                    
                    // Extract the item being moved
                    const [movedItem] = newArray.splice(draggedItem, 1);
                    
                    // Calculate adjusted insertion index (if moving item from before to after)
                    const adjustedIndex = draggedItem < finalDropIndex 
                        ? finalDropIndex - 1  // Account for removed item
                        : finalDropIndex;
                    
                    // Insert at the new position
                    newArray.splice(adjustedIndex, 0, movedItem);
                    
                    return newArray;
                } catch (error) {
                    console.error("Error during item move:", error);
                    return prev;
                }
            });
        }
        
        setDraggedItem(null);
        setDragOverIndex(null);
    };

    // Handle drag end
    const handleDragEnd = (e) => {
        e.target.classList.remove("dragging");
        setDraggedItem(null);
        setDragOverIndex(null);
        
        // Clean up any lingering hover classes
        const elements = document.querySelectorAll(".drag-over-top, .drag-over-bottom");
        elements.forEach(el => {
            el.classList.remove("drag-over-top", "drag-over-bottom");
        });
    };

    // Filter fields based on search term
    const filteredFields = localFieldOrder.filter(field => 
        fields[field.name]?.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="modal-backdrop">
            <div className="column-visibility-dialog">
                <div className="dialog-header">
                    <h3>Show/Hide and Rearrange Columns</h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {!isMobile && <button
                            className="btn btn-sm tour-button"
                            onClick={handleStartTutorial}
                            title="Start tutorial"
                            style={{
                                padding: '4px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                border: '1px solid var(--theme-accent)',
                                color: 'var(--theme-accent)',
                                borderRadius: '4px',
                                background: 'white',
                                cursor: 'pointer'
                            }}
                        >
                            <HelpCircle size={16} />
                            <span>Show Help</span>
                        </button>}
                        <button 
                            className="close-button" 
                            onClick={handleCancel}
                            aria-label="Close"
                        >×</button>
                    </div>
                </div>
                
                <div className="dialog-search">
                    <input 
                        type="text" 
                        className="input-t" 
                        placeholder="Search columns..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                
                <div className="dialog-actions">
                    <button 
                        className="btn-t btn-t-secondary" 
                        onClick={() => handleToggleAll(true)}
                    ><ViewIcon size={ICON_SIZE} />
                        Show All
                    </button>
                    <button 
                        className="btn-t btn-t-secondary" 
                        onClick={() => handleToggleAll(false)}
                    ><HideIcon size={ICON_SIZE} />
                        Hide All
                    </button>
                </div>
                
                <div className="dialog-instructions">
                    <span className="instruction-text">
                        <i className="instruction-icon">↕</i>
                        {isMobile
                            ? "Use the arrows to rearrange column order"
                            : "Drag rows (or use the arrows) to rearrange column order"}
                    </span>
                </div>
                
                <div className="dialog-content">
                    {filteredFields.map((field, index) => {
                        const originalIndex = localFieldOrder.findIndex(f => f.name === field.name);
                        // Only show fields that are marked as public
                        if (fields[field.name]?.public) {
                            return (
                                <div 
                                    key={originalIndex}
                                    className={`column-row ${draggedItem === originalIndex ? 'dragging' : ''} ${dragOverIndex === originalIndex ? 'drag-over' : ''}`}
                                    draggable={true}
                                    onDragStart={(e) => handleDragStart(e, originalIndex)}
                                    onDragOver={(e) => handleDragOver(e, originalIndex)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, originalIndex)}
                                    onDragEnd={handleDragEnd}
                                    data-index={originalIndex}
                                >
                                    <div className="drag-handle">⋮⋮</div>
                                    <div className="checkbox-container">
                                        <input
                                            type="checkbox"
                                            id={`col-${originalIndex}`}
                                            checked={!field.hidden}
                                            onChange={() => handleCheckboxChange(originalIndex)}
                                        />
                                    </div>
                                    <label htmlFor={`col-${originalIndex}`} className="column-label">
                                        {fields[field.name]?.displayName || field.name}
                                    </label>
                                    <div className="column-move-buttons">
                                        <button
                                            type="button"
                                            className="column-move-btn"
                                            onClick={() => moveFieldBy(originalIndex, -1)}
                                            disabled={originalIndex === 0}
                                            aria-label={`Move ${fields[field.name]?.displayName || field.name} up`}
                                        >
                                            <ChevronUp size={16} />
                                        </button>
                                        <button
                                            type="button"
                                            className="column-move-btn"
                                            onClick={() => moveFieldBy(originalIndex, 1)}
                                            disabled={originalIndex === localFieldOrder.length - 1}
                                            aria-label={`Move ${fields[field.name]?.displayName || field.name} down`}
                                        >
                                            <ChevronDown size={16} />
                                        </button>
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })}
                </div>
                
                <div className="dialog-footer">
                    <button 
                        className="btn-t btn-t-secondary" 
                        onClick={handleCancel}
                    ><CancelIcon size={ICON_SIZE} />
                        Cancel
                    </button>
                    <button 
                        className="btn-t btn-t-primary" 
                        onClick={handleApply}
                    ><ConfirmIcon size={ICON_SIZE} />
                        Apply
                    </button>
                </div>
            </div>
            
            {/* Joyride Tour */}
            {!isMobile && <React.Suspense fallback={null}><Joyride
                floaterProps={TOUR_FLOATER_PROPS}
                run={runTour}
                stepIndex={stepIndex}
                steps={steps}
                callback={handleJoyrideCallback}
                continuous
                showProgress
                showSkipButton
                tooltipComponent={CustomTooltip}
                styles={{
                    options: {
                        zIndex: 10000,
                    },
                }}
                disableScrolling={true}
            /></React.Suspense>}
        </div>
    );
}

export { ColumnVisibilityDialog };
export default CommonThead;